import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { protectedResources } from '@src/authConfig'
import { AppContext, useAppStore } from '@src/contexts/AppContext'
import { signIn } from '@src/data/api/user-api/user-api'
import {
  isExpiredToken,
  isImpersonatedSession,
  parseJwt,
  saveUserToken,
  UserDto,
} from '@src/data/providersUtils/UserProviderUtils'
import { reportErrorToConsole } from '@src/services/error-logger'
import { ReactNode, useEffect } from 'react'

interface Props {
  children: ReactNode
}

const UserProvider = ({ children }: Props) => {
  const { instance, accounts, inProgress } = useMsal()
  const appStore = useAppStore()
  useEffect(() => {
    // handle  user sign-in and token acquisition for our application
    const handleUserSignIn = async (accessToken: string) => {
      try {
        const signInResponse = await signIn({ jwt: accessToken })

        // SignIn successful, save the token and update user state
        const icebergToken = parseJwt(signInResponse.jwt) as UserDto
        // In case of expired MSAL cache (never should happen), fail safe
        if (isExpiredToken(icebergToken.exp)) {
          await instance.loginRedirect({
            account: accounts[0],
            scopes: protectedResources.getTokenAzure.scopes.read,
          })
          return
        }
        // Save the token in local storage
        saveUserToken(signInResponse.jwt)
        // Reload the user in the app store for global access and api client reinitialization
        appStore.reloadUser()
      } catch (error) {
        reportErrorToConsole(error)
      }
    }

    const initializeUser = async () => {
      if (!accounts[0] || appStore.user !== null || inProgress !== InteractionStatus.None) {
        return
      }

      if (isImpersonatedSession()) {
        return
      }

      try {
        instance.setActiveAccount(accounts[0])

        // Silent token acquisition
        const response = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: protectedResources.getTokenAzure.scopes.read,
        })

        // Silent token acquisition successful, handle user sign-in
        await handleUserSignIn(response.accessToken)
      } catch (error) {
        // Silent token acquisition failed, try safe redirect login
        // Why it can fail: In some cases MSAL interact with a iframe of our application which fails du to X-Frame-Options Deny
        // Error documentation:
        // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/errors.md#monitor_window_timeout
        reportErrorToConsole(error)
        await instance.loginRedirect({
          account: accounts[0],
          scopes: protectedResources.getTokenAzure.scopes.read,
        })
      }
    }

    initializeUser().catch(reportErrorToConsole)
  }, [accounts, inProgress, appStore, instance])

  if (inProgress !== 'none' || (accounts.length && appStore.user === null && !isImpersonatedSession())) return null
  return <AppContext.Provider value={appStore}>{children}</AppContext.Provider>
}

export default UserProvider
